/**
 * Authenticated fetch helper. Centralises:
 *   - API base URL (relative under nginx, /api/* proxied to :3501)
 *   - Firebase ID token attachment when a user is signed in
 *   - JSON parsing + structured error handling
 */
import { auth } from '../firebase';

export class ApiError extends Error {
  status: number;
  detail?: any;
  constructor(message: string, status: number, detail?: any) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function getIdTokenSafe(): Promise<string | null> {
  const u = auth.currentUser;
  if (!u) return null;
  try { return await u.getIdToken(); } catch { return null; }
}

export interface ApiOptions extends RequestInit {
  requireAuth?: boolean;
  json?: any;
}

export async function api<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers as any) };
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.json);
  }
  const token = await getIdTokenSafe();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.requireAuth && !token) {
    throw new ApiError('Sign-in required.', 401);
  }

  const res = await fetch(path.startsWith('/') ? path : `/${path}`, {
    ...opts,
    headers,
  });

  let data: any = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { data = await res.json(); } catch { /* empty body */ }
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || res.statusText;
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

export interface ProfileUser {
  uid: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  username?: string;
  neighborhood?: string;
  role: 'user' | 'trusted_source' | 'admin';
  trustedReporter: boolean;
  banned?: boolean;
  silencedUntil?: number;
  createdAt: number;
  postCount: number;
}

export const usersApi = {
  sync: () => api<{ user: ProfileUser }>('/api/users/sync', { method: 'POST', requireAuth: true }),
  me: () => api<{ user: ProfileUser }>('/api/users/me', { requireAuth: true }),
  updateMe: (body: { displayName?: string; username?: string; neighborhood?: string }) =>
    api<{ user: ProfileUser }>('/api/users/me', { method: 'POST', json: body, requireAuth: true }),
  neighborhoods: () => api<{ neighborhoods: string[] }>('/api/users/neighborhoods'),
  byUid: (uid: string) => api<{ user: ProfileUser }>(`/api/users/by-uid/${encodeURIComponent(uid)}`),
};
